import { normalizeDemandPage } from '../../agency/normalizeDemandPage';
import { z } from 'zod';

export enum BrandSortDirection {
  Ascending = 'ASC',
  Descending = 'DESC',
}

const nullableStringArray = z.preprocess(
  (value) => (value === null ? [] : value),
  z.array(z.string()).default([]),
);

export const brandSchema = z
  .object({
    id: z.union([z.string(), z.number()]).transform(String),
    name: z.string(),
    agency: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
    industry: z.string().nullable().optional(),
    details: z.string().nullable().optional(),
    url: z.string().nullable().optional(),
    countryCodes: nullableStringArray,
    logoUrl: z.string().nullable().optional(),
    s3LogoCdn: z.string().nullable().optional(),
    createdOn: z.string().nullable().optional(),
    updatedOn: z.string().nullable().optional(),
  })
  .loose()
  .transform(({ s3LogoCdn, ...brand }) => ({
    ...brand,
    logoUrl: brand.logoUrl ?? s3LogoCdn,
  }));

export const brandsPageSchema = z.preprocess(
  normalizeDemandPage,
  z
    .object({
      content: z.array(brandSchema).default([]),
      totalElements: z.number().default(0),
      totalPages: z.number().default(0),
      number: z.number().default(0),
    })
    .loose(),
);

export const countriesSchema = z
  .array(z.object({ code: z.string(), name: z.string() }).loose())
  .default([]);

export type Brand = z.infer<typeof brandSchema>;
export type BrandsPage = z.infer<typeof brandsPageSchema>;
export type Country = z.infer<typeof countriesSchema>[number];

export type BrandsFilter = {
  brandIds?: string[];
  agencyIds?: string[];
  page: number;
  size: number;
  sortProperty: readonly string[];
  sortDirection: BrandSortDirection;
  name: string;
  industries: readonly string[];
};

export type BrandPayload = {
  name: string;
  industry: string;
  details?: string;
  url?: string;
  countryCodes: readonly string[];
  removeLogo?: boolean;
};

export const defaultBrandsFilter: BrandsFilter = {
  page: 0,
  size: 25,
  sortProperty: ['createdOn'],
  sortDirection: BrandSortDirection.Descending,
  name: '',
  industries: [],
};

export function serializeBrandsFilter(filter: BrandsFilter) {
  const params = new URLSearchParams({
    page: String(filter.page),
    size: String(filter.size),
    sortProperty: filter.sortProperty.join(','),
    sortDirection: filter.sortDirection,
  });

  if (filter.brandIds?.length) params.set('brandIds', filter.brandIds.join(','));
  if (filter.name.trim()) params.set('name', filter.name.trim());
  if (filter.industries.length) params.set('industries', filter.industries.join(','));
  return params;
}

export function createBrandMultipartInit(
  method: 'POST' | 'PUT',
  requestKey: 'createRequest' | 'updateRequest',
  payload: BrandPayload,
  logo?: File | null,
): RequestInit {
  const formData = new FormData();
  formData.append(requestKey, new Blob([JSON.stringify(payload)], { type: 'application/json' }));
  formData.append('logo', logo ?? '');
  return { method, body: formData };
}
